
import React, { useState, useEffect, useCallback } from "react";
import Layout from "../components/Layout";
import ApiService from "../services/apiService";
import socketService from "../services/socketService";
import getApiErrorMessage from "../utils/getApiErrorMessage";

// Backend base URL for images (static files served at /uploads) – no /api suffix
const BACKEND_URL = (process.env.REACT_APP_SOCKET_URL || 'http://localhost:5000');

export const sortShops = (shops = []) => {
  const statusPriority = {
    pending: 0,
    active: 1,
    inactive: 2,
    rejected: 3,
  };

  return [...shops].sort((a, b) => {
    const aPriority = statusPriority[a?.status] ?? Number.MAX_SAFE_INTEGER;
    const bPriority = statusPriority[b?.status] ?? Number.MAX_SAFE_INTEGER;
    return aPriority - bPriority;
  });
};

export default function ShopManagement() {
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [successVisible, setSuccessVisible] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [selectedShop, setSelectedShop] = useState(null);
  const [shopDocuments, setShopDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showDocumentRejectModal, setShowDocumentRejectModal] = useState(false);
  const [documentRejectTarget, setDocumentRejectTarget] = useState(null);
  const [documentRejectReason, setDocumentRejectReason] = useState("");
  const [documentRejectReasonType, setDocumentRejectReasonType] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");
  const [rejectionReasonType, setRejectionReasonType] = useState("");
  const [modalMode, setModalMode] = useState("view"); // view, edit, add
  const [formData, setFormData] = useState({
    name: "",
    street: "",
    zone: "",
    barangay: "",
    city: "",
    website: "",
    owner_name: "",
    opening_time: "",
    closing_time: ""
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const parseOperationHours = useCallback((hours) => {
    if (!hours) return { opening_time: '', closing_time: '' };
    const cleaned = String(hours).trim();
    const matches = cleaned.match(/^(\d{1,2}:\d{2})(?:\s*[-–]\s*(\d{1,2}:\d{2}))?$/i);
    if (matches) {
      return {
        opening_time: matches[1] || '',
        closing_time: matches[2] || ''
      };
    }

    const range = cleaned.split('-');
    if (range.length === 2) {
      return {
        opening_time: range[0].trim(),
        closing_time: range[1].trim()
      };
    }

    return { opening_time: cleaned, closing_time: '' };
  }, []);

  const buildOperationHours = useCallback((opening_time, closing_time) => {
    const opening = (opening_time || '').trim();
    const closing = (closing_time || '').trim();
    if (!opening && !closing) return '';
    if (!opening) return closing;
    if (!closing) return opening;
    return `${opening} - ${closing}`;
  }, []);

  const getSavedTimeValue = useCallback((fieldName) => {
    if (!selectedShop?.operation_hours) return '';
    const parsedHours = parseOperationHours(selectedShop.operation_hours);
    if (fieldName === 'opening_time') return parsedHours.opening_time || '';
    return parsedHours.closing_time || '';
  }, [selectedShop, parseOperationHours]);

  const parseAddress = useCallback((address) => {
    if (!address) return { street: '', zone: '', barangay: '', city: '' };
    const parts = String(address)
      .split(',')
      .map(part => part.trim())
      .filter(Boolean);

    return {
      street: parts[0] || '',
      zone: parts[1] || '',
      barangay: parts[2] || '',
      city: parts[3] || ''
    };
  }, []);

  const buildAddress = useCallback((addressFields = {}) => {
    const { street, zone, barangay, city } = addressFields;
    return [street, zone, barangay, city].filter(Boolean).join(', ');
  }, []);

  const loadShops = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      setError(null);
      const shopsData = await ApiService.getShops();
      if (!Array.isArray(shopsData)) {
        throw new Error("Invalid shops response.");
      }
      const nextShops = sortShops(shopsData);
      setShops(nextShops);
      const pendingShops = nextShops.filter(shop => shop.status === 'pending');
      await Promise.all(pendingShops.map(async shop => {
        try {
          await ApiService.getShopDocuments(shop.shop_id);
        } catch (documentError) {
          console.error(`Error loading documents for shop ${shop.shop_id}:`, documentError);
        }
      }));
      setSelectedShop(current => current
        ? nextShops.find(shop => shop.shop_id === current.shop_id) || current
        : current);
    } catch (error) {
      console.error('Error loading shops:', error);
      setError(getApiErrorMessage(error, 'Failed to load shops data.'));
    } finally {
      if (showLoading) setLoading(false);
    }
  }, []);

  // Load shops data and subscribe to database changes broadcast by the backend.
  useEffect(() => {
    loadShops();
  }, [loadShops]);

  useEffect(() => {
    socketService.connect('superadmin');

    const handleShopChange = () => loadShops(false);
    socketService.onShopCreated(handleShopChange);
    socketService.onShopUpdated(handleShopChange);
    socketService.onShopDeleted(handleShopChange);

    return () => {
      socketService.offShopCreated(handleShopChange);
      socketService.offShopUpdated(handleShopChange);
      socketService.offShopDeleted(handleShopChange);
    };
  }, [loadShops]);

  useEffect(() => {
    if (!success) {
      setSuccessVisible(false);
      return;
    }

    setSuccessVisible(true);
    const fadeTimer = window.setTimeout(() => setSuccessVisible(false), 2500);
    const clearTimer = window.setTimeout(() => setSuccess(null), 3000);

    return () => {
      window.clearTimeout(fadeTimer);
      window.clearTimeout(clearTimer);
    };
  }, [success]);

  // Filter shops based on search and status
  const filteredShops = sortShops(
    shops.filter(shop => {
      const matchesSearch = shop.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           shop.owner_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           shop.address?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus = filterStatus === "all" ||
                 (filterStatus === "active" && shop.status === "active") ||
                 (filterStatus === "inactive" && shop.status === "inactive") ||
                 (filterStatus === "pending" && shop.status === "pending") ||
                 (filterStatus === "rejected" && shop.status === "rejected");

      return matchesSearch && matchesStatus;
    })
  );

  // Approve / Reject / Deactivate actions
  const handleApprove = async (shopId) => {
    try {
      // Backend update expects full fields; include existing values to avoid nulling columns
      const shop = shops.find(s => s.shop_id === shopId);
      const payload = {
        name: shop?.name || '',
        address: shop?.address || '',
        website: shop?.website || '',
        owner_name: shop?.owner_name || '',
        operation_hours: buildOperationHours(
          parseOperationHours(shop?.operation_hours).opening_time,
          parseOperationHours(shop?.operation_hours).closing_time
        ) || '',
        status: 'active'
      };
      await ApiService.updateShop(shopId, payload);
      setSuccess('Shop approved successfully.');
      await loadShops();
    } catch (error) {
      console.error('Error approving shop:', error);
      setError(error?.response?.data?.message || 'Failed to approve shop');
    }
  };

  const handleReviewDocument = async (document, status) => {
    try {
      await ApiService.reviewShopDocument(document.document_id, status, '');
      setSuccess(`Document ${status} successfully.`);
      if (selectedShop) {
        const documents = await ApiService.getShopDocuments(selectedShop.shop_id);
        setShopDocuments(documents || []);
      }
    } catch (error) {
      console.error('Error reviewing shop document:', error);
      setError(error?.response?.data?.message || 'Failed to review document');
    }
  };

  const openDocumentRejectModal = (document) => {
    setDocumentRejectTarget(document);
    setDocumentRejectReason("");
    setDocumentRejectReasonType("");
    setShowDocumentRejectModal(true);
  };

  const closeDocumentRejectModal = () => {
    setShowDocumentRejectModal(false);
    setDocumentRejectTarget(null);
    setDocumentRejectReason("");
    setDocumentRejectReasonType("");
  };

  const handleRejectDocumentSubmit = async (event) => {
    event.preventDefault();
    if (!documentRejectTarget) return;

    const reason = documentRejectReasonType === 'Others'
      ? documentRejectReason.trim()
      : documentRejectReasonType;

    if (!reason) {
      setError('Please select or enter a rejection reason for this document.');
      return;
    }

    try {
      await ApiService.reviewShopDocument(documentRejectTarget.document_id, 'rejected', reason);
      setSuccess('Document rejected successfully.');
      closeDocumentRejectModal();
      if (selectedShop) {
        const documents = await ApiService.getShopDocuments(selectedShop.shop_id);
        setShopDocuments(documents || []);
      }
    } catch (error) {
      console.error('Error rejecting shop document:', error);
      setError(error?.response?.data?.message || 'Failed to reject document');
    }
  };

  const openRejectModal = (shop) => {
    setSelectedShop(shop);
    setRejectionReason("");
    setRejectionReasonType("");
    setShowRejectModal(true);
  };

  const closeRejectModal = () => {
    setShowRejectModal(false);
    setRejectionReason("");
    setRejectionReasonType("");
  };

  const handleRejectSubmit = async (event) => {
    event.preventDefault();
    if (!selectedShop) return;

    const reason = rejectionReasonType === 'Others'
      ? rejectionReason.trim()
      : rejectionReasonType;

    if (!reason) {
      setError('Please select or enter a rejection reason.');
      return;
    }

    try {
      await ApiService.rejectShop(selectedShop.shop_id, reason);
      setSuccess('Shop request rejected successfully.');
      closeRejectModal();
      await loadShops();
    } catch (error) {
      console.error('Error rejecting shop:', error);
      setError('Failed to reject shop request');
    }
  };

  // Handle modal operations
  const openModal = async (mode, shop = null) => {
    setModalMode(mode);

    if (mode === 'view' && shop) {
      if (selectedShop?.shop_id !== shop.shop_id) {
        setShopDocuments([]);
      }
      setSelectedShop(shop);
      setDocumentsLoading(true);
      try {
        const documents = await ApiService.getShopDocuments(shop.shop_id);
        if (!Array.isArray(documents)) {
          throw new Error('Invalid shop documents response.');
        }
        setShopDocuments(documents);
      } catch (error) {
        console.error('Error loading shop documents:', error);
        setError(getApiErrorMessage(error, 'Failed to load shop verification documents.'));
      } finally {
        setDocumentsLoading(false);
      }
      setShowModal(false);
      return;
    }

    setSelectedShop(shop);
    if (mode === "add") {
      setFormData({
        name: "",
        street: "",
        zone: "",
        barangay: "",
        city: "",
        website: "",
        owner_name: "",
        opening_time: "",
        closing_time: ""
      });
      setImageFile(null);
      setImagePreview(null);
      setShowModal(true);
      return;
    }

    if (mode === "edit" && shop) {
      const parsedHours = parseOperationHours(shop.operation_hours);
      const parsedAddress = parseAddress(shop.address);
      setFormData({
        name: shop.name || "",
        street: parsedAddress.street || "",
        zone: parsedAddress.zone || "",
        barangay: parsedAddress.barangay || "",
        city: parsedAddress.city || "",
        website: shop.website || "",
        owner_name: shop.owner_name || "",
        opening_time: parsedHours.opening_time || "",
        closing_time: parsedHours.closing_time || ""
      });
      setImageFile(null);
      setImagePreview(shop.logo ? (shop.logo.startsWith('http') ? shop.logo : `${BACKEND_URL}${shop.logo}`) : null);
      setShowModal(false);
      return;
    }

    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedShop(null);
    setModalMode("view");
    setImageFile(null);
    setImagePreview(null);
    setShopDocuments([]);
  };

  const handleEditShop = (shop) => {
    if (!shop) return;
    const parsedHours = parseOperationHours(shop.operation_hours);
    const parsedAddress = parseAddress(shop.address);
    setSelectedShop(shop);
    setModalMode('edit');
    setFormData({
      name: shop.name || "",
      street: parsedAddress.street || "",
      zone: parsedAddress.zone || "",
      barangay: parsedAddress.barangay || "",
      city: parsedAddress.city || "",
      website: shop.website || "",
      owner_name: shop.owner_name || "",
      opening_time: parsedHours.opening_time || "",
      closing_time: parsedHours.closing_time || ""
    });
    setImageFile(null);
    setImagePreview(shop.logo ? (shop.logo.startsWith('http') ? shop.logo : `${BACKEND_URL}${shop.logo}`) : null);
    setShowModal(false);
    setError(null);
  };

  // Handle form submission
  const latestDocumentByType = Array.isArray(shopDocuments)
    ? shopDocuments.reduce((acc, document) => {
        const type = document?.document_type;
        if (!type) return acc;
        const existing = acc[type];
        if (!existing || Number(document.document_id) > Number(existing.document_id)) {
          acc[type] = document;
        }
        return acc;
      }, {})
    : {};

  const effectiveApprovalStatusByType = Object.entries(latestDocumentByType).reduce((acc, [type, latestDocument]) => {
    const sameTypeDocs = shopDocuments.filter(document => document?.document_type === type);
    const hasApproved = sameTypeDocs.some(document => document.status === 'approved');
    const hasRejected = sameTypeDocs.some(document => document.status === 'rejected');
    const hasPending = sameTypeDocs.some(document => document.status === 'pending');

    if (hasApproved) {
      acc[type] = 'approved';
    } else if (hasRejected) {
      acc[type] = 'rejected';
    } else if (hasPending) {
      acc[type] = 'pending';
    } else {
      acc[type] = latestDocument?.status || 'unknown';
    }

    return acc;
  }, {});

  const hasRejectedDocuments = selectedShop && Array.isArray(shopDocuments)
    ? Object.values(effectiveApprovalStatusByType).some(status => status === 'rejected')
    : false;

  const canApproveSelectedShop = selectedShop && selectedShop.status === 'pending' && !hasRejectedDocuments
    && Array.isArray(shopDocuments)
    && shopDocuments.length > 0
    && Object.values(effectiveApprovalStatusByType).length > 0
    && Object.values(effectiveApprovalStatusByType).every(status => status === 'approved');

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        address: buildAddress(formData),
        operation_hours: buildOperationHours(formData.opening_time, formData.closing_time)
      };

      if (modalMode === "add") {
        await ApiService.createShop(payload, imageFile);
        setSuccess('Shop created successfully.');
      } else if (modalMode === "edit" && selectedShop) {
        await ApiService.updateShop(selectedShop.shop_id, payload, imageFile);
        setSuccess('Shop updated successfully.');
      }
      await loadShops(); // Reload shops
      closeModal();
    } catch (error) {
      console.error('Error saving shop:', error);
      setError('Failed to save shop data');
    }
  };

  // Handle delete
  const handleDelete = async (shopId) => {
    if (!window.confirm('Are you sure you want to delete this shop?')) {
      return;
    }
    try {
      await ApiService.deleteShop(shopId);
      setSuccess('Shop deleted successfully.');
      await loadShops(); // Reload shops
    } catch (error) {
      console.error('Error deleting shop:', error);
      setError('Failed to delete shop');
    }
  };

  // Format operation hours
  const formatOperationHours = (hours) => {
    if (!hours) return "Not specified";
    return hours;
  };

  if (loading) {
    return (
      <Layout>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '400px',
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: '50px',
              height: '50px',
              border: '4px solid #e2e8f0',
              borderTop: '4px solid #3b82f6',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 20px'
            }} />
            <p style={{ color: '#64748b', fontSize: '16px' }}>Loading shops data...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div>
        {!selectedShop && (
          <>
            <style>{`
              @media (min-width: 769px) {
                .shop-management-stats-grid {
                  grid-template-columns: repeat(4, minmax(0, 1fr)) !important;
                  gap: 12px !important;
                }

                .shop-management-stats-grid > div {
                  padding: 14px 12px !important;
                  min-height: 84px !important;
                  gap: 8px !important;
                }

                .shop-management-stats-grid > div > div:first-child {
                  width: 34px !important;
                  height: 34px !important;
                  min-width: 34px !important;
                }

                .shop-management-stats-grid > div > div:last-child > div:first-child {
                  font-size: 11px !important;
                }

                .shop-management-stats-grid > div > div:last-child > div:last-child {
                  font-size: 20px !important;
                  letter-spacing: 0.4px !important;
                }

                .shop-management-shop-grid {
                  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)) !important;
                }
              }

              @media (max-width: 480px) {
                .shop-management-page {
                  width: 100% !important;
                  max-width: 100% !important;
                  overflow-x: hidden !important;
                }

                .shop-management-header {
                  padding: 18px 16px !important;
                }

                .shop-management-header h1 {
                  font-size: 22px !important;
                  line-height: 1.2 !important;
                }

                .shop-management-header .shop-management-title-icon {
                  width: 28px !important;
                  height: 28px !important;
                  margin-right: 10px !important;
                }

                .shop-management-header .shop-management-subtitle {
                  display: none !important;
                }

                .shop-management-header .shop-management-summary {
                  display: none !important;
                }

                .shop-management-list {
                  grid-template-columns: 1fr !important;
                }

                .shop-management-search-row {
                  flex-direction: column !important;
                  align-items: stretch !important;
                  gap: 12px !important;
                }

                .shop-management-search-wrap {
                  min-width: 0 !important;
                  width: 100% !important;
                }

                .shop-management-search-wrap input {
                  font-size: 13px !important;
                }

                .shop-management-search-wrap input::placeholder {
                  font-size: 12px !important;
                  opacity: 0.75;
                }

                .shop-management-filter-wrap {
                  width: 100% !important;
                  align-items: center !important;
                }

                .shop-management-filter-wrap span {
                  font-size: 12px !important;
                }

                .shop-management-filter-wrap select {
                  width: 100% !important;
                  font-size: 12px !important;
                  padding: 9px 10px !important;
                }
              }
            `}</style>

            <div className="shop-management-page" style={{
              width: '100%',
              maxWidth: '100%',
              overflowX: 'hidden'
            }}>
              <div className="shop-management-header" style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '25px',
                marginBottom: '25px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                color: 'white'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <h1 style={{ margin: 0, fontSize: '32px', fontWeight: '700', display: 'flex', alignItems: 'center' }}>
                      <span className="shop-management-title-icon" style={{
                        marginRight: '15px',
                        width: '36px',
                        height: '36px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'rgba(255,255,255,0.18)',
                        borderRadius: '8px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                      }}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="7" width="18" height="13" rx="2"/><path d="M16 7V5a4 4 0 0 0-8 0v2"/></svg>
                      </span>
                      Shop Management
                    </h1>
                    <p className="shop-management-subtitle" style={{ margin: '8px 0 0 0', fontSize: '16px', opacity: 0.9 }}>
                      Manage all shops on the eLaba platform
                    </p>
                    <p className="shop-management-summary" style={{ margin: '5px 0 0 0', fontSize: '14px', opacity: 0.8 }}>
                      Total Shops: {shops.length} | Active: {shops.filter(s => s.status === 'active').length} | Pending: {shops.filter(s => s.status === 'pending').length}
                    </p>
                  </div>
                </div>
              </div>

            <div className="shop-management-stats-grid" style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
              gap: '16px',
              marginBottom: '28px'
            }}>
              <div style={{
                minWidth: 0,
                background: 'linear-gradient(120deg, #f8fafc 60%, #e0f2fe 100%)',
                borderRadius: 18,
                padding: '18px 20px',
                boxShadow: '0 4px 24px 0 #0ea5e91a',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                border: '1.5px solid #e0e7ef',
                position: 'relative',
                minHeight: '92px'
              }}>
                <div style={{
                  width: 42,
                  height: 42,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #38bdf8 0%, #0ea5e9 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 8px #0ea5e922',
                  flexShrink: 0
                }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="7" width="18" height="13" rx="2"/><path d="M16 7V5a4 4 0 0 0-8 0v2"/></svg>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, color: '#334155', fontWeight: 600, opacity: 0.85, marginBottom: 2 }}>Total Shops</div>
                  <div style={{ fontSize: 28, fontWeight: 800, color: '#0ea5e9', letterSpacing: 1 }}>{shops.length}</div>
                </div>
              </div>
              <div style={{
                minWidth: 0,
                background: 'linear-gradient(120deg, #f0fdf4 60%, #bbf7d0 100%)',
                borderRadius: 18,
                padding: '18px 20px',
                boxShadow: '0 4px 24px 0 #34d39922',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                border: '1.5px solid #d1fae5',
                position: 'relative',
                minHeight: '92px'
              }}>
                <div style={{
                  width: 42,
                  height: 42,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #6ee7b7 0%, #34d399 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 8px #14b8a622',
                  flexShrink: 0
                }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#065f46" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, color: '#166534', fontWeight: 600, opacity: 0.85, marginBottom: 2 }}>Active Shops</div>
                  <div style={{ fontSize: 28, fontWeight: 800, color: '#22c55e', letterSpacing: 1 }}>{shops.filter(s => s.status === 'active').length}</div>
                </div>
              </div>
              <div style={{
                minWidth: 0,
                background: 'linear-gradient(120deg, #fef2f2 60%, #fca5a5 100%)',
                borderRadius: 18,
                padding: '18px 20px',
                boxShadow: '0 4px 24px 0 #fca5a555',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                border: '1.5px solid #fee2e2',
                position: 'relative',
                minHeight: '92px'
              }}>
                <div style={{
                  width: 42,
                  height: 42,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #fca5a5 0%, #f87171 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 8px #fca5a555',
                  flexShrink: 0
                }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#991b1b" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="7" width="18" height="13" rx="2"/><path d="M16 7V5a4 4 0 0 0-8 0v2"/><line x1="3" y1="21" x2="21" y2="21"/></svg>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, color: '#991b1b', fontWeight: 600, opacity: 0.85, marginBottom: 2 }}>Inactive Shops</div>
                  <div style={{ fontSize: 28, fontWeight: 800, color: '#ef4444', letterSpacing: 1 }}>{shops.filter(s => s.status === 'inactive').length}</div>
                </div>
              </div>
              <div style={{
                minWidth: 0,
                background: 'linear-gradient(120deg, #fefce8 60%, #bae6fd 100%)',
                borderRadius: 18,
                padding: '18px 20px',
                boxShadow: '0 4px 24px 0 #fde68a33',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                border: '1.5px solid #fef9c3',
                position: 'relative',
                minHeight: '92px'
              }}>
                <div style={{
                  width: 42,
                  height: 42,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #fcd34d 0%, #38bdf8 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 8px #fde68a55',
                  flexShrink: 0
                }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#92400e" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, color: '#92400e', fontWeight: 600, opacity: 0.85, marginBottom: 2 }}>Pending Shops</div>
                  <div style={{ fontSize: 28, fontWeight: 800, color: '#f59e42', letterSpacing: 1 }}>{shops.filter(s => s.status === 'pending').length}</div>
                </div>
              </div>
            </div>

            <div className="shop-management-search-row" style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '20px',
              marginBottom: '25px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              display: 'flex',
              gap: '20px',
              alignItems: 'center',
              flexWrap: 'wrap'
            }}>
              <div className="shop-management-search-wrap" style={{ flex: 1, minWidth: 0 }}>
                <input
                  type="text"
                  placeholder="Search shops by name, owner, or address..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '14px',
                    transition: 'border-color 0.2s ease'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                  onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                />
              </div>
              
              <div className="shop-management-filter-wrap" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <span style={{ fontSize: '14px', color: '#64748b', fontWeight: '500' }}>Filter:</span>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  style={{
                    padding: '8px 12px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '6px',
                    fontSize: '14px',
                    backgroundColor: 'white',
                    cursor: 'pointer'
                  }}
                >
                  <option value="all">All Shops</option>
                  <option value="active">Active Only</option>
                  <option value="inactive">Inactive Only</option>
                  <option value="pending">Pending Only</option>
                  <option value="rejected">Rejected Only</option>
                </select>
              </div>
              </div>
            </div>
          </>
        )}

        {/* Error Banner */}
        {error && (
          <div style={{
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center'
          }}>
            <span style={{ color: '#dc2626', marginRight: '8px' }}>⚠</span>
            <span style={{ color: '#dc2626', fontSize: '14px' }}>{error}</span>
            <button type="button" onClick={() => loadShops()} disabled={loading} style={{
              marginLeft: 'auto',
              border: '1px solid #fca5a5',
              borderRadius: '6px',
              background: '#fff',
              color: '#b91c1c',
              padding: '6px 10px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontWeight: 600
            }}>
              Retry
            </button>
          </div>
        )}

        {success && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            display: 'flex',
            justifyContent: 'center',
            paddingTop: '20px',
            zIndex: 1000,
            pointerEvents: 'none'
          }}>
            <div style={{
              background: 'linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 100%)',
              border: '1px solid #86efac',
              color: '#166534',
              padding: '16px 22px',
              borderRadius: '16px',
              boxShadow: '0 14px 34px rgba(6, 95, 70, 0.2)',
              minWidth: '320px',
              maxWidth: '480px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              opacity: successVisible ? 1 : 0,
              transform: successVisible ? 'translateY(0)' : 'translateY(-8px)',
              transition: 'all 0.35s ease'
            }}>
              <div style={{ fontSize: '22px', lineHeight: 1 }}>✓</div>
              <div>
                <div style={{ fontWeight: 800, marginBottom: '3px', fontSize: '16px' }}>Success</div>
                <div style={{ fontSize: '15px' }}>{success}</div>
              </div>
            </div>
          </div>
        )}
        <style>{`
          @media (max-width: 767px) {
            .shop-details-shell .shop-details-grid {
              grid-template-columns: 1fr !important;
            }

            .shop-details-shell .shop-doc-card {
              padding: 14px !important;
            }

            .shop-details-shell .shop-doc-card-top {
              flex-direction: column !important;
              align-items: flex-start !important;
            }

            .shop-details-shell .shop-doc-card-actions {
              width: 100%;
              justify-content: flex-end !important;
            }

            .shop-details-shell .shop-details-actions {
              justify-content: stretch !important;
            }

            .shop-details-shell .shop-details-actions > button {
              flex: 1 1 100% !important;
              min-width: 0 !important;
            }
          }

          @media (max-width: 360px) {
            .shop-details-shell .shop-details-header {
              flex-direction: column !important;
              align-items: flex-start !important;
            }

            .shop-details-shell .shop-details-header-actions {
              width: 100%;
              justify-content: space-between !important;
            }
          }
        `}</style>

        {selectedShop ? (
          modalMode === 'edit' ? (
            <div className="shop-details-shell" style={{ backgroundColor: 'white', borderRadius: '16px', boxShadow: '0 12px 30px rgba(15, 23, 42, 0.06)', border: '1px solid #e2e8f0', overflow: 'hidden', marginBottom: '24px' }}>
              <div style={{ padding: '18px 20px 14px', background: '#f1f5f9', borderBottom: '1px solid #e2e8f0' }}>
                <div className="shop-details-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ fontSize: '11px', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#64748b', fontWeight: 700, marginBottom: '4px' }}>Edit Shop</div>
                    <div style={{ fontSize: '22px', fontWeight: '800', color: '#0f172a' }}>{selectedShop.name || 'Edit Shop'}</div>
                  </div>

                  <div className="shop-details-header-actions" style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      onClick={() => {
                        setModalMode('view');
                        const parsedHours = parseOperationHours(selectedShop.operation_hours);
                        const parsedAddress = parseAddress(selectedShop.address);
                        setFormData({
                          name: selectedShop.name || "",
                          street: parsedAddress.street || "",
                          zone: parsedAddress.zone || "",
                          barangay: parsedAddress.barangay || "",
                          city: parsedAddress.city || "",
                          website: selectedShop.website || "",
                          owner_name: selectedShop.owner_name || "",
                          opening_time: parsedHours.opening_time || "",
                          closing_time: parsedHours.closing_time || ""
                        });
                        setImageFile(null);
                        setImagePreview(selectedShop.logo ? (selectedShop.logo.startsWith('http') ? selectedShop.logo : `${BACKEND_URL}${selectedShop.logo}`) : null);
                      }}
                      style={{
                        padding: '9px 14px',
                        border: '1px solid #94a3b8',
                        background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
                        color: '#0f172a',
                        borderRadius: '10px',
                        fontSize: '12px',
                        fontWeight: '800',
                        cursor: 'pointer',
                        boxShadow: '0 4px 12px rgba(15, 23, 42, 0.08)',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      Back to details
                    </button>
                  </div>
                </div>
              </div>

              <div style={{ padding: '18px 20px 20px' }}>
                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px', maxWidth: '860px', margin: '0 auto' }} encType="multipart/form-data">
                  <div>
                    <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px', display: 'block' }}>
                      Shop Image
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={e => {
                        const file = e.target.files[0];
                        setImageFile(file);
                        if (file) {
                          const reader = new window.FileReader();
                          reader.onload = ev => setImagePreview(ev.target.result);
                          reader.readAsDataURL(file);
                        } else {
                          setImagePreview(null);
                        }
                      }}
                      style={{ marginBottom: '10px' }}
                    />
                    {imagePreview && (
                      <div style={{ marginTop: '8px' }}>
                        <img src={imagePreview} alt="Preview" style={{ width: '140px', height: '140px', borderRadius: '14px', objectFit: 'cover', border: '1px solid #e2e8f0', boxShadow: '0 8px 18px rgba(15, 23, 42, 0.08)' }} />
                      </div>
                    )}
                  </div>

                  <div>
                    <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px', display: 'block' }}>
                      Shop Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      style={{ width: '100%', padding: '12px 16px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '16px' }}
                    />
                  </div>

                  <div>
                    <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px', display: 'block' }}>
                      Owner Name *
                    </label>
                    <input
                      type="text"
                      value={formData.owner_name}
                      onChange={(e) => setFormData({ ...formData, owner_name: e.target.value })}
                      required
                      style={{ width: '100%', padding: '12px 16px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '16px' }}
                    />
                  </div>

                  <div>
                    <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px', display: 'block' }}>
                      Address *
                    </label>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '12px' }}>
                      <div>
                        <input
                          type="text"
                          value={formData.street}
                          onChange={(e) => setFormData({ ...formData, street: e.target.value })}
                          placeholder="Street"
                          required
                          style={{ width: '100%', padding: '12px 16px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '16px' }}
                        />
                      </div>
                      <div>
                        <input
                          type="text"
                          value={formData.zone}
                          onChange={(e) => setFormData({ ...formData, zone: e.target.value })}
                          placeholder="Zone"
                          style={{ width: '100%', padding: '12px 16px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '16px' }}
                        />
                      </div>
                      <div>
                        <input
                          type="text"
                          value={formData.barangay}
                          onChange={(e) => setFormData({ ...formData, barangay: e.target.value })}
                          placeholder="Barangay"
                          style={{ width: '100%', padding: '12px 16px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '16px' }}
                        />
                      </div>
                      <div>
                        <input
                          type="text"
                          value={formData.city}
                          onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                          placeholder="City"
                          required
                          style={{ width: '100%', padding: '12px 16px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '16px' }}
                        />
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div>
                      <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px', display: 'block' }}>
                        Opening Time
                      </label>
                      <input
                        type="time"
                        value={formData.opening_time || getSavedTimeValue('opening_time')}
                        onChange={(e) => setFormData({ ...formData, opening_time: e.target.value })}
                        step="900"
                        style={{ width: '100%', padding: '12px 16px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '16px' }}
                      />
                    </div>

                    <div>
                      <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px', display: 'block' }}>
                        Closing Time
                      </label>
                      <input
                        type="time"
                        value={formData.closing_time || getSavedTimeValue('closing_time')}
                        onChange={(e) => setFormData({ ...formData, closing_time: e.target.value })}
                        step="900"
                        style={{ width: '100%', padding: '12px 16px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '16px' }}
                      />
                    </div>
                  </div>

                  <div>
                    <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px', display: 'block' }}>
                      Website
                    </label>
                    <input
                      type="url"
                      value={formData.website}
                      onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                      style={{ width: '100%', padding: '12px 16px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '16px' }}
                    />
                  </div>

                  <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                    <button
                      type="button"
                      onClick={() => {
                        setModalMode('view');
                        setImageFile(null);
                        setImagePreview(null);
                      }}
                      style={{ flex: 1, padding: '12px 24px', border: '2px solid #e2e8f0', borderRadius: '8px', backgroundColor: 'white', color: '#64748b', fontSize: '16px', fontWeight: '600', cursor: 'pointer' }}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      style={{ flex: 1, padding: '12px 24px', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: '600', cursor: 'pointer' }}
                    >
                      Save changes
                    </button>
                  </div>
                </form>
              </div>
            </div>
          ) : (
            <div className="shop-details-shell" style={{ backgroundColor: 'white', borderRadius: '16px', boxShadow: '0 12px 30px rgba(15, 23, 42, 0.06)', border: '1px solid #e2e8f0', overflow: 'hidden', marginBottom: '24px' }}>
              <div style={{ padding: '18px 20px 14px', background: '#f1f5f9', borderBottom: '1px solid #e2e8f0' }}>
                <div className="shop-details-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ fontSize: '11px', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#64748b', fontWeight: 700, marginBottom: '4px' }}>Shop Details</div>
                    <div style={{ fontSize: '22px', fontWeight: '800', color: '#0f172a' }}>{selectedShop.name || 'Unnamed Shop'}</div>
                  </div>

                  <div className="shop-details-header-actions" style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedShop(null);
                        setShopDocuments([]);
                        setError(null);
                      }}
                      style={{
                        padding: '9px 14px',
                        border: '1px solid #94a3b8',
                        background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
                        color: '#0f172a',
                        borderRadius: '10px',
                        fontSize: '12px',
                        fontWeight: '800',
                        cursor: 'pointer',
                        boxShadow: '0 4px 12px rgba(15, 23, 42, 0.08)',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      Back to shops
                    </button>

                    <span style={{ display: 'inline-flex', alignItems: 'center', padding: '7px 12px', borderRadius: '999px', fontSize: '11px', fontWeight: '800', backgroundColor: selectedShop.status === 'rejected' ? '#fee2e2' : (selectedShop.status === 'inactive' ? '#fef2f2' : (selectedShop.status === 'pending' ? '#fef3c7' : '#dcfce7')), color: selectedShop.status === 'rejected' ? '#b91c1c' : (selectedShop.status === 'inactive' ? '#be123c' : (selectedShop.status === 'pending' ? '#b45309' : '#15803d')), textTransform: 'uppercase', border: selectedShop.status === 'rejected' ? '1px solid #fecaca' : (selectedShop.status === 'inactive' ? '1px solid #fecdd3' : (selectedShop.status === 'pending' ? '1px solid #fde68a' : '1px solid #bbf7d0')) }}>
                      {selectedShop.status === 'rejected' ? 'Rejected' : (selectedShop.status === 'inactive' ? 'Inactive' : (selectedShop.status === 'pending' ? 'Pending' : 'Active'))}
                    </span>

                    {selectedShop.pickup_delivery_enabled === true || selectedShop.pickup_delivery_enabled === 1 || String(selectedShop.pickup_delivery_enabled) === '1' ? (
                      <span
                        style={{
                          position: 'relative',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '26px',
                          height: '26px',
                          borderRadius: '50%',
                          backgroundColor: '#ecfeff',
                          border: '1px solid #a7f3d0',
                          color: '#0f766e',
                          fontSize: '13px',
                          fontWeight: '800',
                          boxShadow: '0 2px 8px rgba(20, 184, 166, 0.15)',
                          cursor: 'default',
                          transition: 'all 0.2s ease',
                        }}
                        aria-label="Pickup Enabled"
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-1px)';
                          e.currentTarget.style.boxShadow = '0 6px 14px rgba(20, 184, 166, 0.18)';
                          const tooltip = e.currentTarget.querySelector('span:last-child');
                          if (tooltip) tooltip.style.opacity = '1';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 2px 8px rgba(20, 184, 166, 0.15)';
                          const tooltip = e.currentTarget.querySelector('span:last-child');
                          if (tooltip) tooltip.style.opacity = '0';
                        }}
                      >
                        <span style={{ display: 'inline-block', transform: 'translateY(-1px)' }}>🚚</span>
                        <span style={{
                          position: 'absolute',
                          left: '50%',
                          bottom: 'calc(100% + 8px)',
                          transform: 'translateX(-50%)',
                          padding: '4px 8px',
                          borderRadius: '8px',
                          backgroundColor: 'rgba(15, 23, 42, 0.86)',
                          color: '#fff',
                          fontSize: '10px',
                          fontWeight: '700',
                          letterSpacing: '0.04em',
                          whiteSpace: 'nowrap',
                          opacity: 0,
                          pointerEvents: 'none',
                          transition: 'opacity 0.18s ease',
                          boxShadow: '0 6px 16px rgba(15, 23, 42, 0.18)',
                        }}>
                          Pickup Enabled
                        </span>
                      </span>
                    ) : null}
                  </div>
                </div>
              </div>

              <div style={{ padding: '18px 20px 20px' }}>
                <div className="shop-details-grid" style={{ display: 'grid', gridTemplateColumns: '1.2fr 0.8fr', gap: '12px', marginBottom: '16px' }}>
                  <div style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '14px 16px' }}>
                    <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: '800', color: '#64748b', marginBottom: '8px' }}>Address</div>
                    <p style={{ margin: 0, color: '#475569', fontSize: '14px', lineHeight: 1.6 }}>
                      {selectedShop.address || 'Address not specified'}
                    </p>
                  </div>

                  <div style={{ background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)', border: '1px solid #bfdbfe', borderRadius: '12px', padding: '14px 16px' }}>
                    <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#1d4ed8', fontWeight: '800', marginBottom: '8px' }}>Details</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '7px', fontSize: '12px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
                        <span style={{ color: '#475569' }}>Owner</span>
                        <strong style={{ textAlign: 'right' }}>{selectedShop.owner_name || 'Not specified'}</strong>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
                        <span style={{ color: '#475569' }}>Hours</span>
                        <strong style={{ textAlign: 'right' }}>{formatOperationHours(selectedShop.operation_hours)}</strong>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
                        <span style={{ color: '#475569' }}>Website</span>
                        <strong style={{ textAlign: 'right', overflowWrap: 'anywhere' }}>
                          {selectedShop.website ? <a href={selectedShop.website} target="_blank" rel="noopener noreferrer" style={{ color: '#1d4ed8', textDecoration: 'none' }}>{selectedShop.website}</a> : 'Not specified'}
                        </strong>
                      </div>
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: '0', backgroundColor: '#fff', borderRadius: '14px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                  <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
                    <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: '800', color: '#64748b' }}>Verification documents</div>
                  </div>
                  <div style={{ padding: '18px' }}>
                    {documentsLoading ? (
                      <p style={{ margin: 0, color: '#64748b', fontSize: '16px' }}>Loading documents...</p>
                    ) : shopDocuments.length === 0 ? (
                      <p style={{ margin: 0, color: '#b45309', fontSize: '16px' }}>No verification documents uploaded.</p>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                        {shopDocuments.map(document => (
                          <div className="shop-doc-card" key={document.document_id} style={{ border: '1px solid #e2e8f0', borderRadius: '12px', padding: '16px 18px', backgroundColor: '#fff', boxShadow: '0 8px 20px rgba(15, 23, 42, 0.02)' }}>
                            <div className="shop-doc-card-top" style={{ display: 'flex', justifyContent: 'space-between', gap: '14px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                              <div style={{ minWidth: 0, flex: 1 }}>
                                <strong style={{ display: 'block', color: '#0f172a', fontSize: '17px', marginBottom: '4px' }}>
                                  {document.document_type === 'business_permit' ? 'Business/Mayor’s Permit' : document.document_type === 'dti_registration' ? 'DTI Business Name Registration' : 'SEC Registration'}
                                </strong>
                                <span style={{ display: 'block', color: '#64748b', fontSize: '13px', overflowWrap: 'anywhere' }}>{document.original_filename}</span>
                              </div>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: document.status === 'approved' ? '#047857' : document.status === 'rejected' ? '#b91c1c' : '#b45309', fontSize: '11px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.08em', backgroundColor: document.status === 'approved' ? '#dcfce7' : document.status === 'rejected' ? '#fee2e2' : '#fef3c7', borderRadius: '999px', padding: '6px 10px', border: document.status === 'approved' ? '1px solid #bbf7d0' : document.status === 'rejected' ? '1px solid #fecaca' : '1px solid #fde68a' }}>
                                {document.status === 'approved' && (
                                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                    <rect x="2" y="2" width="20" height="20" rx="6" fill="#16a34a"/>
                                    <path d="M7 12.5L10.5 16L17 9.5" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                                  </svg>
                                )}
                                {document.status}
                              </span>
                            </div>

                            <div className="shop-doc-card-actions" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginTop: '12px', flexWrap: 'wrap' }}>
                              <a href={document.file_url} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', fontSize: '12px', fontWeight: '700', textDecoration: 'none' }}>Open</a>
                              <div style={{ display: 'flex', gap: '8px', marginLeft: 'auto', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                {document.status === 'pending' && (
                                  <button type="button" onClick={() => handleReviewDocument(document, 'approved')} style={{ border: '1px solid #10b981', background: 'white', color: '#047857', borderRadius: '8px', padding: '6px 10px', cursor: 'pointer', fontWeight: '700', fontSize: '12px' }}>Approve</button>
                                )}
                                {document.status === 'pending' && (
                                  <button type="button" onClick={() => openDocumentRejectModal(document)} style={{ border: '1px solid #ef4444', background: 'white', color: '#b91c1c', borderRadius: '8px', padding: '6px 10px', cursor: 'pointer', fontWeight: '700', fontSize: '12px' }}>Reject</button>
                                )}
                              </div>
                            </div>

                            {document.rejection_reason && (
                              <p style={{ margin: '12px 0 0', color: '#b91c1c', fontSize: '13px', fontWeight: '600' }}>Reason: {document.rejection_reason}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="shop-details-actions" style={{ display: 'flex', gap: '8px', marginTop: '18px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  {selectedShop.status === 'pending' ? (
                    <>
                      <button onClick={() => handleApprove(selectedShop.shop_id)} disabled={!canApproveSelectedShop} style={{ minWidth: '120px', padding: '10px 16px', backgroundColor: canApproveSelectedShop ? '#10b981' : '#94a3b8', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '700', cursor: canApproveSelectedShop ? 'pointer' : 'not-allowed', opacity: canApproveSelectedShop ? 1 : 0.75, boxShadow: '0 8px 16px rgba(16, 185, 129, 0.14)', whiteSpace: 'nowrap' }}>
                        {hasRejectedDocuments ? 'Documents Rejected' : 'Approve'}
                      </button>
                      <button onClick={() => openRejectModal(selectedShop)} style={{ minWidth: '120px', padding: '10px 16px', backgroundColor: '#ef4444', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', boxShadow: '0 8px 16px rgba(239, 68, 68, 0.14)', whiteSpace: 'nowrap' }}>
                        Reject
                      </button>
                    </>
                  ) : (
                    <button onClick={() => handleEditShop(selectedShop)} style={{ minWidth: '120px', padding: '10px 16px', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', boxShadow: '0 8px 16px rgba(59, 130, 246, 0.14)', whiteSpace: 'nowrap' }}>
                      Edit
                    </button>
                  )}
                </div>
              </div>
            </div>
          )
        ) : (
          <div className="shop-management-shop-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px', marginBottom: '30px', width: '100%', maxWidth: '100%' }}>
            {filteredShops.length > 0 ? filteredShops.map((shop, index) => (
              <div key={shop.shop_id || index} style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '20px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                border: '1px solid #e2e8f0',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                height: '100%'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
              }}>
                <div style={{ position: 'absolute', top: '15px', right: '15px', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  <span style={{ padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '600', backgroundColor: shop.status === 'rejected' ? '#dc2626' : (shop.status === 'inactive' ? '#ef4444' : (shop.status === 'pending' ? '#f59e0b' : '#10b981')), color: 'white' }}>
                    {shop.status === 'rejected' ? 'Rejected' : (shop.status === 'inactive' ? 'Inactive' : (shop.status === 'pending' ? 'Pending' : 'Active'))}
                  </span>

                  {shop.pickup_delivery_enabled === true || shop.pickup_delivery_enabled === 1 || String(shop.pickup_delivery_enabled) === '1' ? (
                    <span
                      style={{
                        position: 'relative',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '22px',
                        height: '22px',
                        borderRadius: '50%',
                        backgroundColor: '#ecfeff',
                        border: '1px solid #a7f3d0',
                        color: '#0f766e',
                        fontSize: '11px',
                        fontWeight: '800',
                        lineHeight: 1,
                        boxShadow: '0 2px 6px rgba(20, 184, 166, 0.12)',
                        cursor: 'default',
                        transition: 'all 0.2s ease',
                      }}
                      aria-label="Pickup Enabled"
                      onMouseEnter={(e) => {
                        const tooltip = e.currentTarget.querySelector('span:last-child');
                        if (tooltip) tooltip.style.opacity = '1';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                        e.currentTarget.style.boxShadow = '0 6px 12px rgba(20, 184, 166, 0.16)';
                      }}
                      onMouseLeave={(e) => {
                        const tooltip = e.currentTarget.querySelector('span:last-child');
                        if (tooltip) tooltip.style.opacity = '0';
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 2px 6px rgba(20, 184, 166, 0.12)';
                      }}
                    >
                      <span style={{ display: 'inline-block', transform: 'translateY(-1px)' }}>🚚</span>
                      <span style={{
                        position: 'absolute',
                        left: '50%',
                        bottom: 'calc(100% + 7px)',
                        transform: 'translateX(-50%)',
                        padding: '4px 8px',
                        borderRadius: '8px',
                        backgroundColor: 'rgba(15, 23, 42, 0.86)',
                        color: '#fff',
                        fontSize: '10px',
                        fontWeight: '700',
                        letterSpacing: '0.04em',
                        whiteSpace: 'nowrap',
                        opacity: 0,
                        pointerEvents: 'none',
                        transition: 'opacity 0.18s ease',
                        boxShadow: '0 6px 16px rgba(15, 23, 42, 0.18)',
                      }}>
                        Pickup Enabled
                      </span>
                    </span>
                  ) : null}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                  <div style={{ width: '60px', height: '60px', borderRadius: '12px', backgroundColor: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '15px', fontSize: '24px', overflow: 'hidden' }}>
                    {shop.logo ? (
                      <img src={shop.logo.startsWith('http') ? shop.logo : `${BACKEND_URL}${shop.logo}`} alt={shop.name} style={{ width: '100%', height: '100%', borderRadius: '12px', objectFit: 'cover' }} />
                    ) : (
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="7" width="18" height="13" rx="2"/><path d="M16 7V5a4 4 0 0 0-8 0v2"/></svg>
                    )}
                  </div>

                  <h3 style={{ margin: '0 0 10px 0', fontSize: '18px', fontWeight: '600', color: '#1e293b', lineHeight: '1.4' }}>{shop.name || 'Unnamed Shop'}</h3>

                  <div style={{ marginBottom: '15px', flex: 1 }}>
                    <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="7" r="4"/><path d="M5.5 21a8.38 8.38 0 0 1 13 0"/></svg>
                      <strong>Owner:</strong> <span>{shop.owner_name || 'Not specified'}</span>
                    </p>
                    <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10.5V21a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V10.5"/><path d="M3 10.5 12 4l9 6.5"/></svg>
                      <strong>Address:</strong> <span>{shop.address || 'Not specified'}</span>
                    </p>
                    <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                      <strong>Hours:</strong> <span>{formatOperationHours(shop.operation_hours)}</span>
                    </p>
                    {shop.website && (
                      <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M2 12h20"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10A15.3 15.3 0 0 1 12 2z"/></svg>
                        <a href={shop.website} target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', textDecoration: 'none' }}>Visit Website</a>
                      </p>
                    )}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '8px', marginTop: 'auto', paddingTop: '15px' }}>
                  <button onClick={() => openModal('view', shop)} style={{ flex: 1, padding: '8px 12px', border: '1px solid #3b82f6', borderRadius: '6px', backgroundColor: 'transparent', color: '#3b82f6', fontSize: '14px', fontWeight: '500', cursor: 'pointer', transition: 'all 0.2s ease' }} onMouseEnter={(e) => { e.target.style.backgroundColor = '#3b82f6'; e.target.style.color = 'white'; }} onMouseLeave={(e) => { e.target.style.backgroundColor = 'transparent'; e.target.style.color = '#3b82f6'; }}>View</button>
                  {shop.status === 'pending' ? (
                    <span style={{ flex: 1, padding: '8px 4px', color: '#b45309', fontSize: '12px', fontWeight: '600', textAlign: 'center' }}>Review pending shop</span>
                  ) : (
                    <button onClick={() => openModal('edit', shop)} style={{ flex: 1, padding: '8px 12px', border: '1px solid #3b82f6', borderRadius: '6px', backgroundColor: 'transparent', color: '#3b82f6', fontSize: '14px', fontWeight: '500', cursor: 'pointer', transition: 'all 0.2s ease' }} onMouseEnter={(e) => { e.target.style.backgroundColor = '#3b82f6'; e.target.style.color = 'white'; }} onMouseLeave={(e) => { e.target.style.backgroundColor = 'transparent'; e.target.style.color = '#3b82f6'; }}>Edit</button>
                  )}
                  <button onClick={() => handleDelete(shop.shop_id)} style={{ padding: '8px 12px', border: '1px solid #ef4444', borderRadius: '6px', backgroundColor: 'transparent', color: '#ef4444', fontSize: '14px', fontWeight: '500', cursor: 'pointer', transition: 'all 0.2s ease' }} onMouseEnter={(e) => { e.target.style.backgroundColor = '#ef4444'; e.target.style.color = 'white'; }} onMouseLeave={(e) => { e.target.style.backgroundColor = 'transparent'; e.target.style.color = '#ef4444'; }}>🗑️</button>
                </div>
              </div>
            )) : (
              <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '60px 20px', backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)' }}>
                <div style={{ width: '60px', height: '60px', margin: '0 auto 20px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f1f5f9', borderRadius: '12px' }}>
                  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="7" width="18" height="13" rx="2"/><path d="M16 7V5a4 4 0 0 0-8 0v2"/></svg>
                </div>
                <h3 style={{ margin: '0 0 10px 0', fontSize: '20px', color: '#1e293b' }}>{searchTerm || filterStatus !== 'all' ? 'No shops found' : 'No shops registered yet'}</h3>
                <p style={{ margin: 0, color: '#64748b', fontSize: '16px' }}>{searchTerm || filterStatus !== 'all' ? 'Try adjusting your search or filters' : 'No shops are available for review at the moment.'}</p>
              </div>
            )}
          </div>
        )}

        {/* Modal */}
        {showModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              width: '100%',
              maxWidth: '600px',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
            }}>
              {/* Modal Header */}
              <div style={{
                padding: '25px',
                borderBottom: '1px solid #e2e8f0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <h2 style={{
                  margin: 0,
                  fontSize: '24px',
                  fontWeight: '600',
                  color: '#1e293b',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <span style={{ marginRight: '10px', fontSize: '20px' }}>
                    {modalMode === 'add' ? '➕' : modalMode === 'edit' ? '✏️' : '👁️'}
                  </span>
                  {modalMode === 'add' ? 'Add New Shop' : 
                   modalMode === 'edit' ? 'Edit Shop' : 'Shop Details'}
                </h2>
                <button
                  onClick={closeModal}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '24px',
                    cursor: 'pointer',
                    color: '#64748b',
                    padding: '0',
                    width: '30px',
                    height: '30px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '6px',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#f1f5f9';
                    e.target.style.color = '#1e293b';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = '#64748b';
                  }}
                >
                  ×
                </button>
              </div>

              {/* Modal Content */}
              <div style={{ padding: '25px' }}>
                {modalMode === 'view' && selectedShop ? (
                  // View Mode
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'auto 1fr',
                      gap: '15px',
                      alignItems: 'start'
                    }}>
                      <div style={{
                        width: '80px',
                        height: '80px',
                        borderRadius: '12px',
                        backgroundColor: '#f1f5f9',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '32px',
                        overflow: 'hidden',
                      }}>
                        {selectedShop.logo ? (
                          <img
                            src={selectedShop.logo.startsWith('http') ? selectedShop.logo : `${BACKEND_URL}${selectedShop.logo}`}
                            alt={selectedShop.name}
                            style={{ width: '100%', height: '100%', borderRadius: '12px', objectFit: 'cover' }}
                          />
                        ) : (
                          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="7" width="18" height="13" rx="2"/><path d="M16 7V5a4 4 0 0 0-8 0v2"/></svg>
                        )}
                      </div>
                      <div>
                        <h3 style={{ margin: '0 0 10px 0', fontSize: '24px', fontWeight: '600', color: '#1e293b' }}>
                          {selectedShop.name}
                        </h3>
                        <div style={{
                          display: 'inline-block',
                          padding: '4px 12px',
                          borderRadius: '20px',
                          fontSize: '12px',
                          fontWeight: '600',
                          backgroundColor: selectedShop.status === 'rejected' ? '#dc2626' : (selectedShop.status === 'inactive' ? '#ef4444' : (selectedShop.status === 'pending' ? '#f59e0b' : '#10b981')),
                          color: 'white'
                        }}>
                          {selectedShop.status === 'rejected' ? 'Rejected' : (selectedShop.status === 'inactive' ? 'Inactive' : (selectedShop.status === 'pending' ? 'Pending' : 'Active'))}
                        </div>
                      </div>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                      <div>
                        <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '5px', display: 'block' }}>
                          Owner Name
                        </label>
                        <p style={{ margin: 0, fontSize: '16px', color: '#1e293b', padding: '8px 0', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="7" r="4"/><path d="M5.5 21a8.38 8.38 0 0 1 13 0"/></svg>
                          {selectedShop.owner_name || 'Not specified'}
                        </p>
                      </div>

                      <div>
                        <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '5px', display: 'block' }}>
                          Address
                        </label>
                        <p style={{ margin: 0, fontSize: '16px', color: '#1e293b', padding: '8px 0', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10.5V21a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V10.5"/><path d="M3 10.5 12 4l9 6.5"/></svg>
                          {selectedShop.address || 'Not specified'}
                        </p>
                      </div>

                      <div>
                        <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '5px', display: 'block' }}>
                          Operation Hours
                        </label>
                        <p style={{ margin: 0, fontSize: '16px', color: '#1e293b', padding: '8px 0', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                          {formatOperationHours(selectedShop.operation_hours)}
                        </p>
                      </div>

                      {selectedShop.website && (
                        <div>
                          <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '5px', display: 'block' }}>
                            Website
                          </label>
                          <p style={{ margin: 0, fontSize: '16px', color: '#1e293b', padding: '8px 0', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M2 12h20"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10A15.3 15.3 0 0 1 12 2z"/></svg>
                            <a href={selectedShop.website} target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', textDecoration: 'none' }}>
                              {selectedShop.website}
                            </a>
                          </p>
                        </div>
                      )}

                      <div style={{ marginTop: '8px' }}>
                        <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px', display: 'block' }}>
                          Verification Documents
                        </label>
                        {documentsLoading ? (
                          <p style={{ margin: 0, color: '#64748b' }}>Loading documents...</p>
                        ) : shopDocuments.length === 0 ? (
                          <p style={{ margin: 0, color: '#b45309' }}>No verification documents uploaded.</p>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            {shopDocuments.map(document => (
                              <div key={document.document_id} style={{ border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', alignItems: 'flex-start' }}>
                                  <div style={{ minWidth: 0 }}>
                                    <strong style={{ display: 'block', color: '#1e293b' }}>
                                      {document.document_type === 'business_permit'
                                        ? 'Business/Mayor’s Permit'
                                        : document.document_type === 'dti_registration'
                                          ? 'DTI Business Name Registration'
                                          : 'SEC Registration'}
                                    </strong>
                                    <span style={{ display: 'block', color: '#64748b', fontSize: '12px', overflowWrap: 'anywhere' }}>
                                      {document.original_filename}
                                    </span>
                                  </div>
                                  <span style={{ color: document.status === 'approved' ? '#047857' : document.status === 'rejected' ? '#b91c1c' : '#b45309', fontSize: '12px', fontWeight: '700', textTransform: 'uppercase' }}>
                                    {document.status}
                                  </span>
                                </div>
                                <div style={{ display: 'flex', gap: '8px', marginTop: '10px', flexWrap: 'wrap' }}>
                                  <a href={document.file_url} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', fontSize: '13px', fontWeight: '600' }}>
                                    Open document
                                  </a>
                                  {document.status === 'pending' && (
                                    <button type="button" onClick={() => handleReviewDocument(document, 'approved')} style={{ border: '1px solid #10b981', background: 'white', color: '#047857', borderRadius: '6px', padding: '4px 8px', cursor: 'pointer' }}>
                                      Approve
                                    </button>
                                  )}
                                  {document.status === 'pending' && (
                                    <button type="button" onClick={() => handleReviewDocument(document, 'rejected')} style={{ border: '1px solid #ef4444', background: 'white', color: '#b91c1c', borderRadius: '6px', padding: '4px 8px', cursor: 'pointer' }}>
                                      Reject
                                    </button>
                                  )}
                                </div>
                                {document.rejection_reason && (
                                  <p style={{ margin: '8px 0 0', color: '#b91c1c', fontSize: '12px' }}>
                                    Reason: {document.rejection_reason}
                                  </p>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                      {selectedShop.status === 'pending' ? (
                        <>
                          <button
                            onClick={() => handleApprove(selectedShop.shop_id)}
                            disabled={!canApproveSelectedShop}
                            style={{
                              flex: 1,
                              padding: '12px 24px',
                              backgroundColor: canApproveSelectedShop ? '#10b981' : '#94a3b8',
                              color: 'white',
                              border: 'none',
                              borderRadius: '8px',
                              fontSize: '16px',
                              fontWeight: '600',
                              cursor: canApproveSelectedShop ? 'pointer' : 'not-allowed',
                              transition: 'all 0.2s ease',
                              opacity: canApproveSelectedShop ? 1 : 0.7,
                            }}
                            onMouseEnter={(e) => {
                              if (canApproveSelectedShop) {
                                e.target.style.backgroundColor = '#059669';
                                e.target.style.transform = 'translateY(-1px)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (canApproveSelectedShop) {
                                e.target.style.backgroundColor = '#10b981';
                                e.target.style.transform = 'translateY(0)';
                              }
                            }}
                          >
                            {hasRejectedDocuments ? 'Documents Rejected' : 'Approve'}
                          </button>
                          <button
                            onClick={() => openRejectModal(selectedShop)}
                            style={{
                              flex: 1,
                              padding: '12px 24px',
                              backgroundColor: '#ef4444',
                              color: 'white',
                              border: 'none',
                              borderRadius: '8px',
                              fontSize: '16px',
                              fontWeight: '600',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = '#dc2626';
                              e.target.style.transform = 'translateY(-1px)';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = '#ef4444';
                              e.target.style.transform = 'translateY(0)';
                            }}
                          >
                            Reject
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => {
                            setModalMode('edit');
                            const parsedHours = parseOperationHours(selectedShop.operation_hours);
                            const parsedAddress = parseAddress(selectedShop.address);
                            setFormData({
                              name: selectedShop.name || "",
                              street: parsedAddress.street || "",
                              zone: parsedAddress.zone || "",
                              barangay: parsedAddress.barangay || "",
                              city: parsedAddress.city || "",
                              website: selectedShop.website || "",
                              owner_name: selectedShop.owner_name || "",
                              opening_time: parsedHours.opening_time || "",
                              closing_time: parsedHours.closing_time || ""
                            });
                            setImageFile(null);
                            setImagePreview(selectedShop.logo ? (selectedShop.logo.startsWith('http') ? selectedShop.logo : `${BACKEND_URL}${selectedShop.logo}`) : null);
                            setShowModal(false);
                          }}
                          style={{
                            flex: 1,
                            padding: '12px 24px',
                            backgroundColor: '#3b82f6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '16px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#2563eb';
                            e.target.style.transform = 'translateY(-1px)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = '#3b82f6';
                            e.target.style.transform = 'translateY(0)';
                          }}
                        >
                          Edit Shop
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  // Add/Edit Form
                  <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px', maxWidth: '920px', margin: '0 auto' }} encType="multipart/form-data">
                    {/* Shop Image Upload */}
                    <div>
                      <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px', display: 'block' }}>
                        Shop Image
                      </label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={e => {
                          const file = e.target.files[0];
                          setImageFile(file);
                          if (file) {
                            const reader = new window.FileReader();
                            reader.onload = ev => setImagePreview(ev.target.result);
                            reader.readAsDataURL(file);
                          } else {
                            setImagePreview(null);
                          }
                        }}
                        style={{ marginBottom: '10px' }}
                      />
                      {imagePreview && (
                        <div style={{ marginTop: '8px' }}>
                          <img src={imagePreview} alt="Preview" style={{ width: '140px', height: '140px', borderRadius: '14px', objectFit: 'cover', border: '1px solid #e2e8f0', boxShadow: '0 8px 18px rgba(15, 23, 42, 0.08)' }} />
                        </div>
                      )}
                    </div>
                    <div>
                      <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px', display: 'block' }}>
                        Shop Name *
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          border: '2px solid #e2e8f0',
                          borderRadius: '8px',
                          fontSize: '16px',
                          transition: 'border-color 0.2s ease'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                        onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                        placeholder="Enter shop name"
                      />
                    </div>

                    <div>
                      <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px', display: 'block' }}>
                        Owner Name *
                      </label>
                      <input
                        type="text"
                        value={formData.owner_name}
                        onChange={(e) => setFormData({ ...formData, owner_name: e.target.value })}
                        required
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          border: '2px solid #e2e8f0',
                          borderRadius: '8px',
                          fontSize: '16px',
                          transition: 'border-color 0.2s ease'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                        onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                        placeholder="Enter owner name"
                      />
                    </div>

                    <div>
                      <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px', display: 'block' }}>
                        Address *
                      </label>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '12px' }}>
                        <input
                          type="text"
                          value={formData.street}
                          onChange={(e) => setFormData({ ...formData, street: e.target.value })}
                          required
                          placeholder="Street"
                          style={{
                            width: '100%',
                            padding: '12px 16px',
                            border: '2px solid #e2e8f0',
                            borderRadius: '8px',
                            fontSize: '16px',
                            transition: 'border-color 0.2s ease'
                          }}
                          onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                          onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                        />
                        <input
                          type="text"
                          value={formData.zone}
                          onChange={(e) => setFormData({ ...formData, zone: e.target.value })}
                          placeholder="Zone"
                          style={{
                            width: '100%',
                            padding: '12px 16px',
                            border: '2px solid #e2e8f0',
                            borderRadius: '8px',
                            fontSize: '16px',
                            transition: 'border-color 0.2s ease'
                          }}
                          onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                          onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                        />
                        <input
                          type="text"
                          value={formData.barangay}
                          onChange={(e) => setFormData({ ...formData, barangay: e.target.value })}
                          placeholder="Barangay"
                          style={{
                            width: '100%',
                            padding: '12px 16px',
                            border: '2px solid #e2e8f0',
                            borderRadius: '8px',
                            fontSize: '16px',
                            transition: 'border-color 0.2s ease'
                          }}
                          onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                          onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                        />
                        <input
                          type="text"
                          value={formData.city}
                          onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                          placeholder="City"
                          required
                          style={{
                            width: '100%',
                            padding: '12px 16px',
                            border: '2px solid #e2e8f0',
                            borderRadius: '8px',
                            fontSize: '16px',
                            transition: 'border-color 0.2s ease'
                          }}
                          onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                          onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                        />
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      <div>
                        <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px', display: 'block' }}>
                          Opening Time
                        </label>
                        <input
                          type="time"
                          value={formData.opening_time || getSavedTimeValue('opening_time')}
                          onChange={(e) => setFormData({ ...formData, opening_time: e.target.value })}
                          step="900"
                          style={{
                            width: '100%',
                            padding: '12px 16px',
                            border: '2px solid #e2e8f0',
                            borderRadius: '8px',
                            fontSize: '16px',
                            transition: 'border-color 0.2s ease'
                          }}
                          onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                          onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                        />
                      </div>

                      <div>
                        <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px', display: 'block' }}>
                          Closing Time
                        </label>
                        <input
                          type="time"
                          value={formData.closing_time || getSavedTimeValue('closing_time')}
                          onChange={(e) => setFormData({ ...formData, closing_time: e.target.value })}
                          step="900"
                          style={{
                            width: '100%',
                            padding: '12px 16px',
                            border: '2px solid #e2e8f0',
                            borderRadius: '8px',
                            fontSize: '16px',
                            transition: 'border-color 0.2s ease'
                          }}
                          onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                          onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                        />
                      </div>
                    </div>

                    <div>
                      <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px', display: 'block' }}>
                        Website
                      </label>
                      <input
                        type="url"
                        value={formData.website}
                        onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          border: '2px solid #e2e8f0',
                          borderRadius: '8px',
                          fontSize: '16px',
                          transition: 'border-color 0.2s ease'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                        onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                        placeholder="https://www.example.com"
                      />
                    </div>



                    <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                      <button
                        type="button"
                        onClick={closeModal}
                        style={{
                          flex: 1,
                          padding: '12px 24px',
                          border: '2px solid #e2e8f0',
                          borderRadius: '8px',
                          backgroundColor: 'white',
                          color: '#64748b',
                          fontSize: '16px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.borderColor = '#64748b';
                          e.target.style.color = '#1e293b';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.borderColor = '#e2e8f0';
                          e.target.style.color = '#64748b';
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        style={{
                          flex: 1,
                          padding: '12px 24px',
                          backgroundColor: '#3b82f6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          fontSize: '16px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#2563eb';
                          e.target.style.transform = 'translateY(-1px)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = '#10b981';
                          e.target.style.transform = 'translateY(0)';
                        }}
                      >
                        {modalMode === 'add' ? 'Add Shop' : 'Update Shop'}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        )}

        {showDocumentRejectModal && documentRejectTarget && (
          <div
            role="presentation"
            onClick={(event) => {
              if (event.target === event.currentTarget) closeDocumentRejectModal();
            }}
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(15, 23, 42, 0.58)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1100,
              padding: '20px'
            }}
          >
            <form
              onSubmit={handleRejectDocumentSubmit}
              role="dialog"
              aria-modal="true"
              aria-labelledby="reject-document-title"
              onClick={(event) => event.stopPropagation()}
              style={{
                backgroundColor: 'white',
                borderRadius: '14px',
                width: '100%',
                maxWidth: '480px',
                padding: '25px',
                boxShadow: '0 20px 45px rgba(15, 23, 42, 0.22)'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px' }}>
                <div>
                  <h2 id="reject-document-title" style={{ margin: 0, color: '#1e293b', fontSize: '22px' }}>
                    Reject document
                  </h2>
                  <p style={{ margin: '8px 0 0', color: '#64748b', lineHeight: 1.5 }}>
                    Select a reason for rejecting <strong>{documentRejectTarget.original_filename || 'this document'}</strong>.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={closeDocumentRejectModal}
                  aria-label="Close document reject modal"
                  style={{ background: 'none', border: 'none', color: '#64748b', fontSize: '24px', cursor: 'pointer', lineHeight: 1 }}
                >
                  ×
                </button>
              </div>

              <label htmlFor="document-rejection-reason-type" style={{ display: 'block', marginTop: '22px', marginBottom: '8px', color: '#334155', fontWeight: '600' }}>
                Reason <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <select
                id="document-rejection-reason-type"
                value={documentRejectReasonType}
                onChange={(event) => {
                  setDocumentRejectReasonType(event.target.value);
                  if (event.target.value !== 'Others') setDocumentRejectReason('');
                }}
                required
                style={{ width: '100%', boxSizing: 'border-box', padding: '11px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', backgroundColor: 'white', fontFamily: 'inherit', fontSize: '14px', color: '#1e293b' }}
              >
                <option value="" disabled>Select a reason</option>
                <option value="Incomplete or unreadable document">Incomplete or unreadable document</option>
                <option value="Document is blurry or low quality">Document is blurry or low quality</option>
                <option value="Document is expired or invalid">Document is expired or invalid</option>
                <option value="Information does not match the shop profile">Information does not match the shop profile</option>
                <option value="Others">Others</option>
              </select>

              {documentRejectReasonType === 'Others' && (
                <textarea
                  id="document-rejection-reason"
                  value={documentRejectReason}
                  onChange={(event) => setDocumentRejectReason(event.target.value)}
                  placeholder="Enter the reason for rejecting this document"
                  rows={4}
                  required
                  style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical', marginTop: '10px', padding: '11px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontFamily: 'inherit', fontSize: '14px', color: '#1e293b' }}
                />
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '22px' }}>
                <button
                  type="button"
                  onClick={closeDocumentRejectModal}
                  style={{ padding: '10px 16px', border: '1px solid #cbd5e1', borderRadius: '7px', background: 'white', color: '#475569', fontWeight: '600', cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{ padding: '10px 16px', border: 'none', borderRadius: '7px', background: '#dc2626', color: 'white', fontWeight: '600', cursor: 'pointer' }}
                >
                  Confirm rejection
                </button>
              </div>
            </form>
          </div>
        )}

        {showRejectModal && selectedShop && (
          <div
            role="presentation"
            onClick={(event) => {
              if (event.target === event.currentTarget) closeRejectModal();
            }}
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(15, 23, 42, 0.58)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1100,
              padding: '20px'
            }}
          >
            <form
              onSubmit={handleRejectSubmit}
              role="dialog"
              aria-modal="true"
              aria-labelledby="reject-shop-title"
              onClick={(event) => event.stopPropagation()}
              style={{
                backgroundColor: 'white',
                borderRadius: '14px',
                width: '100%',
                maxWidth: '480px',
                padding: '25px',
                boxShadow: '0 20px 45px rgba(15, 23, 42, 0.22)'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px' }}>
                <div>
                  <h2 id="reject-shop-title" style={{ margin: 0, color: '#1e293b', fontSize: '22px' }}>
                    Reject shop request
                  </h2>
                  <p style={{ margin: '8px 0 0', color: '#64748b', lineHeight: 1.5 }}>
                    Are you sure you want to reject <strong>{selectedShop.name || 'this shop'}</strong>?
                  </p>
                </div>
                <button
                  type="button"
                  onClick={closeRejectModal}
                  aria-label="Close reject modal"
                  style={{ background: 'none', border: 'none', color: '#64748b', fontSize: '24px', cursor: 'pointer', lineHeight: 1 }}
                >
                  ×
                </button>
              </div>

              <label htmlFor="rejection-reason-type" style={{ display: 'block', marginTop: '22px', marginBottom: '8px', color: '#334155', fontWeight: '600' }}>
                Reason <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <select
                id="rejection-reason-type"
                value={rejectionReasonType}
                onChange={(event) => {
                  setRejectionReasonType(event.target.value);
                  if (event.target.value !== 'Others') setRejectionReason('');
                }}
                required
                style={{ width: '100%', boxSizing: 'border-box', padding: '11px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', backgroundColor: 'white', fontFamily: 'inherit', fontSize: '14px', color: '#1e293b' }}
              >
                <option value="" disabled>Select a reason</option>
                <option value="Incomplete required shop information">Incomplete required shop information</option>
                <option value="Invalid shop name or owner information">Invalid shop name or owner information</option>
                <option value="Invalid or incomplete shop address">Invalid or incomplete shop address</option>
                <option value="Invalid operating hours">Invalid operating hours</option>
                <option value="Others">Others</option>
              </select>

              {rejectionReasonType === 'Others' && (
                <textarea
                  id="rejection-reason"
                  value={rejectionReason}
                  onChange={(event) => setRejectionReason(event.target.value)}
                  placeholder="Enter the reason for rejecting this request"
                  rows={4}
                  required
                  style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical', marginTop: '10px', padding: '11px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontFamily: 'inherit', fontSize: '14px', color: '#1e293b' }}
                />
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '22px' }}>
                <button
                  type="button"
                  onClick={closeRejectModal}
                  style={{ padding: '10px 16px', border: '1px solid #cbd5e1', borderRadius: '7px', background: 'white', color: '#475569', fontWeight: '600', cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{ padding: '10px 16px', border: 'none', borderRadius: '7px', background: '#dc2626', color: 'white', fontWeight: '600', cursor: 'pointer' }}
                >
                  Confirm rejection
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </Layout>
  );
}